import {SmallLoader} from '@kit';

import React from 'react';

import {EditHoc} from '../../../components/edit-hoc';
import {getApolloError, humanEnumeration, uppercaseFirstLetter} from '../../../components/utils';
import {Markdown, MarkdownInline} from '../../../components/utils/md';
import {abilityToString} from '../../../components/enums';
import {useClassQuery, ImageTarget} from '../../../generated/types';
import {Gallery} from '../../../components/gallery';
import {OtherTitles} from '../../../components/other-titles';
import {formatClassChooseAndProps} from '../../../components/render-helpers';
import {CommentTargetType, Comments} from '../../../components/comments';

import pageCss from '../../../components/common.module.css';
import gridCss from '../../../components/grid.module.css';
import markdownCss from '../../../components/utils/md/markdown.module.css';

type Props = {
	classId: string;
};

export const ClassAbout: React.FC<Props> = ({classId}) => {
	const {data, error, loading} = useClassQuery({variables: {id: classId}, ssr: true});
	const heroClass = data?.class;

	const props = formatClassChooseAndProps(heroClass?.choose, heroClass?.props);

	return (
		<>
			{loading && (
				<div className={pageCss.fullSizeBlock}>
					<SmallLoader />
				</div>
			)}
			{error && <div className={pageCss.fullSizeBlock}>{getApolloError(error).message}</div>}
			{heroClass && (
				<div>
					<EditHoc
						load={() => import('../edit-buttons').then((m) => m.EditAbout)}
						editProps={{id: heroClass.id}}
					>
						<h4>Ключевые аттрибуты {uppercaseFirstLetter(heroClass.cases.genitive)}</h4>
						<table className={pageCss.grid}>
							<tbody>
								<tr>
									<td>Основная характеристика</td>
									<td>{humanEnumeration(heroClass.primaryAbility.map(abilityToString))}</td>
								</tr>
								<tr>
									<td>Кость здоровья</td>
									<td>
										D{heroClass.hitDie} за уровень {uppercaseFirstLetter(heroClass.cases.genitive)}
									</td>
								</tr>
								<tr>
									<td>Умения в испытаниях</td>
									<td>{humanEnumeration(heroClass.saves.map(abilityToString))}</td>
								</tr>
								<tr>
									<td>Умения в навыках</td>
									<td>
										<Markdown text={props.skill} />
									</td>
								</tr>
								<tr>
									<td>Умения в оружии</td>
									<td>{props.weaponProficiency}</td>
								</tr>
								{(props.musicalInstrumentProficiency || props.toolsProficiency) && (
									<tr>
										<td>Умения в инструментах</td>
										<td>{props.musicalInstrumentProficiency || props.toolsProficiency}</td>
									</tr>
								)}
								<tr>
									<td>Обращение с бронёй</td>
									<td>{props.armorProficiency ? props.armorProficiency : '—'}</td>
								</tr>
								<tr>
									<td>Начальная экипировка</td>
									<td>
										<Markdown text={heroClass.equipment} />
									</td>
								</tr>
							</tbody>
						</table>
						<Markdown text={heroClass.description} />
						<h2>Выбор {uppercaseFirstLetter(heroClass.cases.genitive)}…</h2>
						<h3>На 1-м уровне</h3>
						<Markdown
							text={`
						* Получите все атрибуты из таблицы «Ключевые атрибуты ${uppercaseFirstLetter(heroClass.cases.genitive)}».
						* Получите особенности ${uppercaseFirstLetter(
							heroClass.cases.genitive,
						)} 1-го уровня, перечисленные в таблице «Особенности ${uppercaseFirstLetter(
								heroClass.cases.genitive,
							)}».
						`}
						/>

						<h3>При мультиклассировании</h3>
						<Markdown
							text={heroClass.multiClassing
								.split('\n')
								.map((l) => `* ${l}`)
								.join('\n')}
						/>
					</EditHoc>

					<div className={markdownCss.margin}>
						<div className={markdownCss.indent}>
							<OtherTitles originalTitle={heroClass.originalTitle} />
						</div>
					</div>

					<Gallery images={heroClass.images || []} target={{type: ImageTarget.Class, id: classId}} />

					{heroClass && heroClass.trainingTable && heroClass.trainingTable?.length > 0 && (
						<div>
							<h3>Причины</h3>
							<p>
								Если вы еще не определились с классом, самое время сделать это сейчас, приняв во
								внимание происхождение и другие детали, которые вы уже проработали. Как только вы
								сделаете выбор, бросьте d6 и найдите выпавшее число в соответствующей таблице этого
								раздела, которая описывает, как вы могли стать представителем своего класса.
							</p>
							<div className={gridCss.grid} style={{gridTemplateColumns: 'max-content 1fr'}}>
								<div className={gridCss.headers}>
									{heroClass.trainingTable.slice(0, 1)![0]!.map((column) => (
										<td key={column} className={gridCss.header}>
											<MarkdownInline text={column || ''} />
										</td>
									))}
								</div>
								<tbody className={gridCss.body}>
									{heroClass.trainingTable.slice(1).map((row, rowIndex) => (
										<tr key={rowIndex} className={gridCss.row}>
											{row!.map((column) => (
												<td key={column} className={gridCss.cell}>
													<MarkdownInline text={column || ''} />
												</td>
											))}
										</tr>
									))}
								</tbody>
							</div>
							<p>
								<b>Источник:</b> <i>«Руководство Занатара по всему»</i>, стр. 66.
							</p>
						</div>
					)}
				</div>
			)}
			{heroClass && <Comments targetType={CommentTargetType.Class} targetId={classId} />}
		</>
	);
};
